import { createClient } from '@supabase/supabase-js';

import { getSupabaseAuthStorage } from '@/lib/mobile/secure-storage-adapter';
import { isDesktopPlatform } from '@/lib/desktop/bridge';
import { pinFunctionRegion } from '@/lib/function-region';
import { routeFunctionRequest } from '@/lib/routed-functions';

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';

let client: SupabaseClient<Database> | null = null;

function getClient(): SupabaseClient<Database> {
  client ??= createClient<Database>(
    String(import.meta.env.VITE_SUPABASE_URL),
    String(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY),
    {
      auth: {
        flowType: isDesktopPlatform() ? 'pkce' : 'implicit',
        storage: getSupabaseAuthStorage(),
        persistSession: true,
        autoRefreshToken: true,
        // Passkey methods throw at call time unless this flag is set. Experimental
        // API: the exact pin and its lockstep bump procedure are guarded via
        // .github/dependabot.yml and the passkey plan under docs/plans/.
        experimental: { passkey: true },
      },
      global: {
        // Resolve fetch at call time so dev tools can intercept via window.fetch patching.
        // Calls to a function routed to core-functions leave Supabase here; the
        // rest run in the database's region.
        fetch: async (...args: Parameters<typeof fetch>): Promise<Response> =>
          await globalThis.fetch(...pinFunctionRegion(...routeFunctionRequest(...args))),
      },
    },
  );
  return client;
}

// Construction is deferred to first property access: `import.meta.env` is only
// substituted by Vite, so building the client at module scope throws
// `Invalid supabaseUrl` in any non-Vite bundler that reaches this module.
export const supabase = new Proxy({} as SupabaseClient<Database>, {
  get(_target, prop): unknown {
    // The client is not a thenable, but the promise resolution procedure probes
    // `then` on anything it resolves — answering that without building the client
    // keeps `await`ing (or resolving a promise with) this proxy side-effect free.
    if (prop === 'then') {
      return undefined;
    }
    const instance = getClient();
    const value = Reflect.get(instance, prop) as unknown;
    if (typeof value !== 'function') {
      return value;
    }
    // supabase-js methods rely on `this`; a bare Reflect.get returns them unbound.
    const method = value as (...args: unknown[]) => unknown;
    return method.bind(instance) as unknown;
  },
  has: (_target, prop) => Reflect.has(getClient(), prop),
  getOwnPropertyDescriptor(_target, prop) {
    const descriptor = Reflect.getOwnPropertyDescriptor(getClient(), prop);
    return descriptor && { ...descriptor, configurable: true };
  },
  ownKeys: () => Reflect.ownKeys(getClient()),
});
